import type { FC } from "react";

import classNames from "classnames";

import { useConfig } from "@jti/app-context/useConfig";
import { useCheckoutConfiguration } from "@jti/commerce/providers";
import type { OrderPrices } from "@jti/magento";
import { useSelector } from "@jti/store/reduxHooks";
import { useTaxesDisplayMode, useTranslations } from "@jti/ui";

import { PriceBlock } from "../PriceBlock";

import styles from "../Prices.module.scss";

type RegularCartPricesProps = {
  prices?: OrderPrices | null;
  isSummary?: boolean;
  isFreeTrial?: boolean;
  className?: string;
};

export const RegularCartPrices: FC<RegularCartPricesProps> = ({ prices, isSummary = false, className }) => {
  const { __ } = useTranslations();
  const { showPricesWithTaxExcluded = false } = useConfig();

  const {
    subtotalIncludingTax,
    appliedTaxes,
    grandTotal,
    shipping,
    estimatedShippingCost,
    discounts,
    appliedCampaign,
    subtotal,
  } = prices || {};
  const { taxInfo: additionalTaxInfo } = useCheckoutConfiguration();
  const taxesDisplayMode = useTaxesDisplayMode();

  const { currency: shopCurrency } = useSelector((state) => state.cart);
  const pricesFetchStatus = useSelector((state) => state.cart.prices.fetchStatus);

  const isPriceLoading = pricesFetchStatus === "pending";
  const priceToUse = showPricesWithTaxExcluded ? subtotal : subtotalIncludingTax;
  const shippingToDisplay = shipping ?? estimatedShippingCost;

  return (
    <>
      <div className={classNames(styles.pricesColumns, className)}>
        <PriceBlock
          testId="cartSubtotal"
          currency={priceToUse?.currency}
          shopCurrency={shopCurrency}
          value={priceToUse?.value || 0}
          title={__("cart.prices.subtotal")}
          inProgress={isPriceLoading}
        />
        {appliedCampaign?.loyalty_discount_uuid && (
          <PriceBlock
            testId="loyaltyRedemptionValue"
            currency={priceToUse?.currency}
            shopCurrency={shopCurrency}
            value={appliedCampaign?.loyalty_discount_amount}
            title={`${__("cart.prices.loyalty.points_redemption")}`}
            inProgress={isPriceLoading}
            isDiscount
          />
        )}
        {discounts?.map(({ label, coupon_code = "", amount: { currency, value } }) => (
          <PriceBlock
            key={`${label}#${value}`}
            testId="promoCodeValue"
            currency={currency}
            shopCurrency={shopCurrency}
            value={value}
            inProgress={isPriceLoading}
            title={coupon_code?.length ? `${__("cart.prices.promo_code")} ${label}` : label}
            isDiscount
          />
        ))}
        {shippingToDisplay && (
          <PriceBlock
            testId="shippingAmount"
            currency={shippingToDisplay.currency}
            shopCurrency={shopCurrency}
            value={shippingToDisplay.value || 0}
            title={__("cart.prices.shipping")}
            inProgress={isPriceLoading}
          />
        )}

        {taxesDisplayMode.showBreakdownWithNames &&
          appliedTaxes &&
          appliedTaxes?.length > 0 &&
          appliedTaxes.map((tax, i) => (
            <PriceBlock
              key={tax.label}
              currency={tax.amount.currency}
              shopCurrency={shopCurrency}
              value={tax.amount.value}
              title={`${tax.label} ${tax.rate ? `(${tax.rate}%)` : ""}`}
              inProgress={isPriceLoading}
              topSeparator={i === 0}
            />
          ))}
      </div>

      <PriceBlock
        testId="cartTotal"
        currency={grandTotal?.currency}
        shopCurrency={shopCurrency}
        value={grandTotal?.value || 0}
        title={__("cart.prices.total.title")}
        totalTitle={true}
        isSummary={isSummary}
        tax={taxesDisplayMode.showSimpleTotalBreakdown ? appliedTaxes : []}
        taxInfo={additionalTaxInfo}
        topSeparator={true}
        inProgress={isPriceLoading}
      />
    </>
  );
};
